/**
 * Filters for activities.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { ACTIVITY_STATUS_CONFIG } from './activity-configs';
import type { ActivityFilters } from './activity-configs';
import { getActivityTypes } from '@/services/activity-types.service';
import type { ActivityType } from '@/types/activity-type.types';

/** Props for {@link ActivitiesFiltersContent}. */
interface ActivitiesFiltersContentProps {
  filters: ActivityFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onToggleActivityType: (activityTypeId: string) => void;
  onSetSearchQuery?: (query: string) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Multi-select status chips (`active` / `inactive`). */
const StatusFilter: React.FC<{
  filters: ActivityFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(ACTIVITY_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
              } as React.CSSProperties
            }
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Multi-select chips from the activity-type catalog. */
const ActivityTypeFilter: React.FC<{
  filters: ActivityFilters;
  onToggleActivityType: (activityTypeId: string) => void;
  enabled: boolean;
}> = ({ filters, onToggleActivityType, enabled }) => {
  const { data: activityTypes = [], isLoading } = useQuery<ActivityType[]>({
    queryKey: ['activityTypes'],
    queryFn: async () => {
      const response = await getActivityTypes();
      return response.data;
    },
    enabled,
  });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Type d&apos;activité</span>
      <div className="filter-options flex flex-wrap gap-2 max-w-full">
        {isLoading && <span className="text-sm text-muted-foreground">Chargement des types…</span>}
        {!isLoading &&
          activityTypes.map((at) => {
            const isActive = filters.activityTypeIds.includes(at.id);
            return (
              <Button
                key={at.id}
                variant="custom"
                size="sm"
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                  isActive ? 'filter-option-primary--active' : 'filter-option-primary',
                )}
                onClick={() => onToggleActivityType(at.id)}
              >
                {at.label}
              </Button>
            );
          })}
      </div>
    </div>
  );
};

/** Label / code search (not debounced — parent resets page on each keystroke). */
const SearchFilter: React.FC<{
  filters: ActivityFilters;
  onSetSearchQuery: (query: string) => void;
}> = ({ filters, onSetSearchQuery }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="relative w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        placeholder="Nom de l'activité..."
        value={filters.searchQuery ?? ''}
        onChange={(e) => onSetSearchQuery(e.target.value)}
        onClear={() => onSetSearchQuery('')}
      />
    </div>
  </div>
);

/** Filters for activities. */
export const ActivitiesFiltersContent: React.FC<ActivitiesFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onToggleActivityType,
  onSetSearchQuery,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-type-activity') && (
        <ActivityTypeFilter
          filters={filters}
          onToggleActivityType={onToggleActivityType}
          enabled={true}
        />
      )}
      {show('filter-search') && onSetSearchQuery && (
        <SearchFilter filters={filters} onSetSearchQuery={onSetSearchQuery} />
      )}
    </div>
  );
};
